import React, { useEffect, useState, useCallback} from 'react';
import {
  Switch,
  Route,
} from "react-router-dom";
import { csvParse } from 'd3-dsv';
import { mean } from 'd3-array';
import { format } from 'd3-format';
import { sortBy } from 'lodash';
import * as topojson from 'topojson-client';

import { getCountryByName, getCountryById } from './countryLookup.js';
import { getSubnationalUnitByName, getSubnationalUnitById } from './subnationalLookup.js';
import Viz from './Viz';

// population
//
import countryPopulationsFile from './data/populations/UNCountryPopulations.csv.js';
import canadaPopulationsFile from './data/populations/canada-populations.csv.js';
import australianPopulationsFile from './data/populations/australian-populations.csv.js';
import ukPopulationsFile from './data/populations/ukPopulations.csv.js';
import southAfricanPopulationsFile from './data/populations/south-africa-populations.csv.js';
import germanPopulationsFile from './data/populations/germany-populations.csv.js';
import nigerianPopulationsFile from './data/populations/nigeria-populations.csv.js';
import indianPopulationsFile from './data/populations/india-populations.csv.js';
import brazilianPopulationsFile from './data/populations/brazilian-populations.csv.js';
import koreanPopulationsFile from './data/populations/korean-populations.csv.js';
import pakistaniPopulationsFile from './data/populations/pakistani-populations.csv.js';
import spainPopulationsFile from './data/populations/spain-populations.csv.js';
import italyPopulationsFile from './data/populations/italy-populations.csv.js';
import mexicoPopulationsFile from './data/populations/mexico-populations.csv.js';
import japanPopulationsFile from './data/populations/japan-populations.csv.js'
import peruPopulationsFile from './data/populations/peru-populations.csv.js'
import colombiaPopulationsFile from './data/populations/colombia-populations.csv.js'
import netherlandsPopulationsFile from './data/populations/netherlands-populations.csv.js'
import ukrainePopulationsFile from './data/populations/ukraine-populations.csv.js'
import chilePopulationsFile from './data/populations/chile-populations.csv.js'
import chinaPopulationsFile from './data/populations/china-populations.csv.js'
//
// population

// geo
//

// load
//
// countries
import countriesTopo from './data/countries-110m.json'
import countriesHighResTopo from './data/countries-50m.json'
// admin1
import canadaTopo from './data/canadaprovtopo.json'
import australiaTopo from './data/aus_state.json'
import ukTopo from './data/ukCountriesRegions_0.5.topo.json';
import irelandTopo from './data/ireland-counties.topo.json';
import germanyTopo from './data/germany-states.topo.json'
// import nigeriaTopo from './data/nigeria-states.json'
import indiaTopo from './data/india.topo.json'
// import southAfricaTopo from './data/south-africa-provinces.json'
// import brazilTopo from './data/br-states.json'
import brazilTopo from './data/brazil.simple.topo.json'
// import koreaTopo from './data/skorea-provinces-topo.json'
import pakistanTopo from './data/pakistan-provinces.json'
import spainTopo from './data/spain-comunidad-with-canary-islands.topo.json'
import italyTopo from './data/italy-regions.topo.json'
import mexicoTopo from './data/mexico_states.json'
import japanTopo from './data/jp-prefectures.json'
import peruTopo from './data/peru-departments.json'
import colombiaTopo from './data/colombia.topo.json'
import netherlandsTopo from './data/netherlands.topo.json'
import ukraineTopo from './data/Ukraine-regions.json'
import chileTopo from './data/chile.topo.json'
import chinaTopo from './data/china-provinces.json'
//
// load

// format
//
countriesTopo.objects.countries.geometries = countriesTopo.objects.countries.geometries
// countriesTopo.objects.ne_50m_admin_0_countries.geometries = countriesTopo.objects.ne_50m_admin_0_countries.geometries
  .filter(c => (c.id !== '010') && c.properties.name !== 'Hong Kong' && c.properties.name !== 'Macao')
  .map((c, i) => {
    c.id = c.id || `?-${i}`;
    c.id = c.id.toString();
    // c.id = c.properties.ISO_N3 || `?-${i}`;
    const country = getCountryById(c.id) || { id: '000', name: c.properties.name, code: 'missing from countryLookup' };
    c.properties.name = country.name;
    c.properties.id = country.id;
    return c;
  });

countriesHighResTopo.objects.countries.geometries = countriesHighResTopo.objects.countries.geometries
  .filter(c => (c.id !== '010' && c.properties.name !== 'Siachen Glacier') && c.properties.name !== 'Hong Kong' && c.properties.name !== 'Macao')
  .map((c, i) => {
    c.id = c.id || `?-${i}`;
    c.id = c.id.toString();
    const country = getCountryById(c.id) || { id: '000', name: c.properties.name, code: 'missing from countryLookup' };
    c.properties.name = country.name;
    c.properties.id = country.id;
    return c;
  });

// const countries = topojson.feature(countriesTopo, countriesTopo.objects.ne_50m_admin_0_countries).features;
const countries = topojson.feature(countriesTopo, countriesTopo.objects.countries).features;
const countriesHighRes = topojson.feature(countriesHighResTopo, countriesHighResTopo.objects.countries).features;
// manually add higher-res Ireland to match scale of UK Countries
const irelandID = '372';
const irelandIndex = countriesHighRes.findIndex(c => c.id === irelandID);
const ireland = topojson.merge(irelandTopo, irelandTopo.objects.IRL_adm1.geometries);
ireland.id = '372'
ireland.properties = countriesHighRes[irelandIndex].properties;
countriesHighRes[irelandIndex] = ireland;
//

const canadianSubnational = topojson.feature(canadaTopo, canadaTopo.objects.canadaprov).features;
const ausObjMap = { 0: 'AU-NSW', 1: 'AU-VIC', 2: 'AU-QLD', 3: 'AU-SA', 4: 'AU-WA', 5: 'AU-TAS', 6: 'AU-NT', 7: 'AU-ACT', 8: 'AU-Other' };
australiaTopo.objects.aus_state.geometries.map(r => {
  if (ausObjMap[r.id]) {
    r.id = ausObjMap[r.id];
  }
  return r;
});
const australianSubnational = topojson.feature(australiaTopo, australiaTopo.objects.aus_state).features;

// const saObjMap = { 1: 'ZA-EC', 2: 'ZA-FS', 3: 'ZA-GP', 4: 'ZA-KZN', 5: 'ZA-LP', 6: 'ZA-MP', 7: 'ZA-NW', 8: 'ZA-NC', 10: 'ZA-WC' };
// southAfricaTopo.objects.ZAF_adm1.geometries.map(r => {
//   if (saObjMap[r.properties.ID_1]) {
//     r.id = saObjMap[r.properties.ID_1];
//   }
//   return r;
// });
// const southAfricaSubnational = topojson.feature(southAfricaTopo, southAfricaTopo.objects.ZAF_adm1).features;

// nigeriaTopo.objects.NGA_adm1.geometries.map(r => {
//   const name = r.properties.NAME_1 === 'Nassarawa' ? 'Nasarawa' : r.properties.NAME_1;
//   const region = getSubnationalUnitByName(name, '566');
//   if (region) {
//     r.id = region.id;
//   }
//   return r;
// });
// const nigeriaSubnational = topojson.feature(nigeriaTopo, nigeriaTopo.objects.NGA_adm1).features

indiaTopo.objects.india.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.ST_NAME, '356');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const indiaSubnational = topojson.feature(indiaTopo, indiaTopo.objects.india).features;

const deObjMap = { 'BW': 'DE-BW', 'BY': 'DE-BY', 'BE': 'DE-BE', 'BR': 'DE-BB', 'HB': 'DE-HB', 'HH': 'DE-HH', 'HE': 'DE-HE', 'MV': 'DE-MV', 'NI': 'DE-NI', 'NW': 'DE-NW', 'RP': 'DE-RP', 'SL': 'DE-SL', 'SN': 'DE-SN', 'ST': 'DE-ST', 'SH': 'DE-SH', 'TH': 'DE-TH' };
germanyTopo.objects['germany-states.geo'].geometries.map(r => {
  if (deObjMap[r.properties.short]) {
    r.id = deObjMap[r.properties.short];
  }
  return r;
});
const germanySubnational = topojson.feature(germanyTopo, germanyTopo.objects['germany-states.geo']).features

brazilTopo.objects.states.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.name, '076');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const brazilSubnational = topojson.feature(brazilTopo, brazilTopo.objects.states).features;

// koreaTopo.objects.skorea_provinces_2018_geo.geometries.map(r => {
//   const region = getSubnationalUnitByName(r.properties.name_eng, '410');
//   if (region) {
//     r.id = region.id;
//   }
//   return r;
// });
// const koreaSubnational = topojson.feature(koreaTopo, koreaTopo.objects.skorea_provinces_2018_geo).features;

pakistanTopo.objects.PAK_adm1.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.name, '586');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const pakistanSubnational = topojson.feature(pakistanTopo, pakistanTopo.objects.PAK_adm1).features;


spainTopo.objects.ESP_adm1.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.NAME_1, '724');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const spainSubnational = topojson.feature(spainTopo, spainTopo.objects.ESP_adm1).features;

italyTopo.objects.ITA_adm1.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.NAME_1, '380');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const italySubnational = topojson.feature(italyTopo, italyTopo.objects.ITA_adm1).features;

mexicoTopo.objects['mexico_states.geo'].geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.state_name, '484');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const mexicoSubnational = topojson.feature(mexicoTopo, mexicoTopo.objects['mexico_states.geo']).features;

japanTopo.objects.JPN_adm1.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.NAME_1, '392');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const japanSubnational = topojson.feature(japanTopo, japanTopo.objects.JPN_adm1).features;

peruTopo.objects["peru-departments'"].geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.NOMBDEP, '604');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const peruSubnational = topojson.feature(peruTopo, peruTopo.objects["peru-departments'"]).features;

colombiaTopo.objects.collection.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.NAME_1, '170');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const colombiaSubnational = topojson.feature(colombiaTopo, colombiaTopo.objects.collection).features;

netherlandsTopo.objects['the-netherlands.geojson'].geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.name, '528');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const netherlandsSubnational = topojson.feature(netherlandsTopo, netherlandsTopo.objects['the-netherlands.geojson']).features;

ukraineTopo.objects.UKR_adm1.geometries.map(r => {
  const region = getSubnationalUnitByName(`${r.properties.NAME_1} ${r.properties.TYPE_1.replace("'",'')}`, '804');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const ukraineSubnational = topojson.feature(ukraineTopo, ukraineTopo.objects.UKR_adm1).features;

// filter out undemarcated ice fields on border with Argentina
chileTopo.objects.regiones.geometries = chileTopo.objects.regiones.geometries.filter(r => r.properties.NOM_REG !== 'Zona sin demarcar')
chileTopo.objects.regiones.geometries.map(r => {
  const region = getSubnationalUnitByName(r.properties.NOM_REG.replace(/Región del? /, ''), '152');
  if (region) {
    r.id = region.id;
  }
  return r;
});
const chileSubnational = topojson.feature(chileTopo, chileTopo.objects.regiones).features;

chinaTopo.objects.collection.geometries.map(r => {
  const region = getSubnationalUnitByName(`${r.properties.NAME_1} ${r.properties.ENGTYPE_1}`, '156');
  if (region) {
    r.id = region.id;
  } else {
    // console.warn('not found', r.properties)
  }
  return r;
});
const chinaSubnational = topojson.feature(chinaTopo, chinaTopo.objects.collection).features;

// UK
// setup matching ids
const ukCombinedObjMap = {
  'UKC': 'UK-NE',
  'UKD': 'UK-NW',
  'UKE': 'UK-YatH',
  'UKF': 'UK-EM',
  'UKG': 'UK-WM',
  'UKH': 'UK-E',
  'UKI': 'UK-L',
  'UKJ': 'UK-SE',
  'UKK': 'UK-SW',
  'UKL': 'GB-WLS',
  'UKM': 'GB-SCT',
  'UKN': 'GB-NIR',
};
ukTopo.objects.NUTS_Level_1__January_2018__Boundaries.geometries.map(r => {
  if (ukCombinedObjMap[r.properties.nuts118cd]) {
    r.id = ukCombinedObjMap[r.properties.nuts118cd];
  }
  return r;
});
// create England by merge
const england = topojson.merge(ukTopo, ukTopo.objects.NUTS_Level_1__January_2018__Boundaries.geometries.filter(d => d.id.includes('UK-')));
england.id = 'GB-ENG';
england.properties = {};
const ukCombinedSubnational = topojson.feature(ukTopo, ukTopo.objects.NUTS_Level_1__January_2018__Boundaries).features.concat(england);

// add more topojson features here w/ concat
const subnationalFeatures = (
    canadianSubnational
    .concat(australianSubnational)
    .concat(ukCombinedSubnational)
    // .concat(southAfricaSubnational)
    .concat(germanySubnational)
    // .concat(nigeriaSubnational)
    .concat(indiaSubnational)
    .concat(brazilSubnational)
    // .concat(koreaSubnational)
    .concat(pakistanSubnational)
    .concat(spainSubnational)
    .concat(italySubnational)
    .concat(mexicoSubnational)
    .concat(japanSubnational)
    .concat(peruSubnational)
    .concat(colombiaSubnational)
    .concat(netherlandsSubnational)
    .concat(ukraineSubnational)
    .concat(chileSubnational)
    .concat(chinaSubnational)
  )
  .map((region, i) => {
    region.id = region.id || `subnational-???-${i}`;
    const subnationalUnit = getSubnationalUnitById(region.id);
    if (subnationalUnit) {
      region.properties.name = subnationalUnit.name;
      region.properties.id = subnationalUnit.id;
    } else {
      console.warn(`missing lookup for ${region.id}`);
    }
    return region;
  });
//
// format

//
// geo

export default function WorldContainer(props) {
  const [data, setData]  = useState({});
  const [dateUpdated, setDateUpdated] = useState(null);
  const [countryPopulations, setCountryPopulations]  = useState(null);
  const [regionsById, setRegionsById] = useState({});
  const [regionDataByKey, setRegionDataByKey] = useState({});
  const [countryNonZeroValues, setCountryNonZeroValues] = useState({});

  const formatDateString = useCallback((date) => {
    const zeroPadDayMonth = format('0=2');
    return `${date.getFullYear()}-${zeroPadDayMonth(date.getMonth() + 1)}-${zeroPadDayMonth(date.getDate())}`;
  }, []);

  useEffect(() => {
    const populationByCountryId = {};
    let numLoaded = 0;

    function allLoaded() {
      setCountryPopulations(populationByCountryId);
    }

    const populationFiles = [
      countryPopulationsFile,
      canadaPopulationsFile,
      ukPopulationsFile,
      australianPopulationsFile,
      southAfricanPopulationsFile,
      germanPopulationsFile,
      nigerianPopulationsFile,
      indianPopulationsFile,
      brazilianPopulationsFile,
      koreanPopulationsFile,
      pakistaniPopulationsFile,
      spainPopulationsFile,
      italyPopulationsFile,
      mexicoPopulationsFile,
      japanPopulationsFile,
      netherlandsPopulationsFile,
      ukrainePopulationsFile,
      chilePopulationsFile,
      chinaPopulationsFile,
    ];
    const rows = csvParse(countryPopulationsFile)
      .map(row => {
        const country = getCountryByName(row['Country']);
        row.country = country || { id: '000', name: row.Country, jhName: '', whoName: row.Country, code: 'missing from countryLookup' };
        row.id = row.country.id;
        row.name = row.country.name;
        row.population = row.Value * 1000;
        return row;
      });
    const populationRows = rows
      .filter(row => row.id !== '000');
    populationRows.forEach(row => {
      populationByCountryId[row.id] = row;
    });
    numLoaded++;
    if (numLoaded === populationFiles.length) {
      allLoaded();
    }

    const subnationalPopulationFiles = [
      { file: canadaPopulationsFile, countryId: '124', nameAccessor: d => d.Geography, populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: ukPopulationsFile, countryId: '826', nameAccessor: d => d.Name, populationAccessor: row => +row.Population.replace(/,/g, '')},
      { file: australianPopulationsFile, countryId: '036', nameAccessor: d => d.Geography, populationAccessor: row => +row['Population']},
      { file: southAfricanPopulationsFile, countryId: '710', nameAccessor: d => d.Province, populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: germanPopulationsFile, countryId: '276', nameAccessor: d => d.State, populationAccessor: row => +row['2018'].replace(/,/g, '')},
      { file: nigerianPopulationsFile, countryId: '566', nameAccessor: d => d.STATE, populationAccessor: row => +row['2016']},
      { file: indianPopulationsFile, countryId: '356', nameAccessor: d => d['State'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: brazilianPopulationsFile, countryId: '076', nameAccessor: d => d['Region'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: koreanPopulationsFile, countryId: '410', nameAccessor: d => d['Region'], populationAccessor: row => +row['2018'].replace(/,/g, '')},
      { file: pakistaniPopulationsFile, countryId: '586', nameAccessor: d => d['Region'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: spainPopulationsFile, countryId: '724', nameAccessor: d => d['Region'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: italyPopulationsFile, countryId: '380', nameAccessor: d => d['Region'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: mexicoPopulationsFile, countryId: '484', nameAccessor: d => d['State'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: japanPopulationsFile, countryId: '392', nameAccessor: d => d['Prefecture'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: peruPopulationsFile, countryId: '604', nameAccessor: d => d['Region'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: colombiaPopulationsFile, countryId: '170', nameAccessor: d => d['Department'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: netherlandsPopulationsFile, countryId: '528', nameAccessor: d => d['Province'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: ukrainePopulationsFile, countryId: '804', nameAccessor: d => d['Region'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: chilePopulationsFile, countryId: '152', nameAccessor: d => d['Name'], populationAccessor: row => +row['Population'].replace(/,/g, '')},
      { file: chinaPopulationsFile, countryId: '156', nameAccessor: d => d['Province'], populationAccessor: row => +row['Population'].replace(/,/g, '')},

    ];

    subnationalPopulationFiles.forEach(subnationalPopulation => {
      const rows = csvParse(subnationalPopulation.file)
        .map(row => {
          const region = getSubnationalUnitByName(subnationalPopulation.nameAccessor(row), subnationalPopulation.countryId);
          if (region) {
            return {
              region,
              id: region.id,
              name: region.name,
              parent: region.countryId,
              population: subnationalPopulation.populationAccessor(row),
            };
          } else {
            // console.log('skipping', subnationalPopulation.nameAccessor(row), subnationalPopulation.countryId)
          }
          return null;
        }).filter(row => row);
      rows.forEach(row => {
        populationByCountryId[row.id] = row;
      });
      //
      numLoaded++;
      if (numLoaded === populationFiles.length) {
        allLoaded();
      }
    });

  }, []);

  useEffect(() => {

    // load JH/CDC dateUpdated (github)
    window.fetch(`${process.env.PUBLIC_URL}/data/jh-timestamp.txt`)
      .then(value => value.text())
      .then(date => setDateUpdated(new Date(date).toLocaleString()));

    const hopkinsFiles = [
      'confirmed', 'deaths'
    ];
    const ukFiles = ['cases'] //, 'deaths']

    const hopkinsKeyMaps = {
      'confirmed': 'cases',
      'deaths': 'deaths',
    };

    // load cdc data
    let numLoaded = 0
    function allLoaded(files) {
      const regionKeys = ['Province/State','Country/Region']
      const regions = {}
      const moreThanZeroCases = {};
      hopkinsFiles.forEach(file => {
        const fileData = files[file];
        //
        const ukMaxPriority = {};
        fileData
          .filter(row => row['Country/Region'] === 'United Kingdom')
          .forEach(row => {
            if (ukMaxPriority[row['Province/State']]) {
              ukMaxPriority[row['Province/State']] = Math.max(ukMaxPriority[row['Province/State']], row.ukPriority);
            } else {
              ukMaxPriority[row['Province/State']] = row.ukPriority;
            }
          });
        //
        fileData
          .filter(row => {
            if (row['Country/Region'] === 'United Kingdom' && ukMaxPriority[row['Province/State']]) {
              return row.ukPriority >= ukMaxPriority[row['Province/State']];
            }
            return true;
          })
          .forEach(row => {
            const regionKey = `${row[regionKeys[0]] !== ''  ? `${row[regionKeys[0]]}, ` : '' }${row[regionKeys[1]]}`
            if (!regions[regionKey]) {
              regions[regionKey] = {
                [regionKeys[0]]: row[regionKeys[0]],
                [regionKeys[1]]: row[regionKeys[1]],
                Lat: +row.Lat,
                Long: +row.Long,
                dataMap: {},
                key: regionKey,
                Combined_Key: regionKey,
                source: 'hopkins-summary',
              }
            }
            const region = regions[regionKey];
            Object.keys(row).forEach(column => {
              if(column.match(/[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{1,2}/)) {
                const rawDayKey = column.replace(/\//g, '-');
                const dateParts = rawDayKey.split('-');
                const year = + dateParts[2]
                // the cron job writes `2020` as `20`, but `2021` as `2021` so we add 2000 years to dates if year is low
                const yearOffset = year < 1000 ? 2000 : 0
                const date = new Date(yearOffset + (+dateParts[2]), dateParts[0] - 1, dateParts[1]);

                const dayKey = formatDateString(date);
                if (!moreThanZeroCases[regionKey]) {
                  moreThanZeroCases[regionKey] = row[column] > 0;
                }
                if (moreThanZeroCases[regionKey]) {
                  const finalDataKey = hopkinsKeyMaps[file] ? hopkinsKeyMaps[file] : file;
                  if (!region.dataMap[dayKey]) {
                    const tempMap = {
                      dateString: dayKey,
                      date,
                    };
                    tempMap[finalDataKey] = +row[column] || 0;
                    if ('cases' in tempMap) {
                      region.dataMap[dayKey] = tempMap;
                    }
                  } else {
                    region.dataMap[dayKey][finalDataKey] = +row[column] || 0;
                  }
                }
              }
            })
          })
      })

      const regionArray = Object.keys(regions).map(region => {
        regions[region].data = sortBy(Object.keys(regions[region].dataMap).map(key => regions[region].dataMap[key]), 'dateString');
        return regions[region]
      }).filter(region => {
        return region.data.length > 0
      })
      const aggregates = ['Australia', 'Canada', 'China'] // for now only aggregate these countries

      aggregates.forEach(countryName => {
        const aggregateRows = regionArray.filter(d => {
          return d['Country/Region'] === countryName && d['Region/State'] !== ''
        })
        const aggregateData = {
          [regionKeys[0]]: '',
          [regionKeys[1]]: countryName,
          dataMap: {},
          key: countryName,
          Combined_Key: countryName,
          source: 'hopkins-summary',
          rowFromAggregate: true,
        }
        aggregateRows.forEach(aggregateRow => {
          aggregateRow.aggregateRow = true
          aggregateRow.data.forEach(dayData => {
            const day = dayData.dateString
            if (!aggregateData.dataMap[day]) {
              aggregateData.dataMap[day] = {
                dateString: dayData.dateString,
                date: dayData.date,
              }
            }
            hopkinsFiles.forEach(file => {
              const finalDataKey = hopkinsKeyMaps[file] ? hopkinsKeyMaps[file] : file
              if (! (finalDataKey in aggregateData.dataMap[day])) {
                aggregateData.dataMap[day][finalDataKey] = 0
              }
              aggregateData.dataMap[day][finalDataKey] += dayData[finalDataKey]
            })
          })
        })
        regions[countryName] = aggregateData
        aggregateData.data = sortBy(Object.keys(aggregateData.dataMap).map(key => aggregateData.dataMap[key]), 'dateString');
        regionArray.push(aggregateData)
      })

      regionArray.forEach(region => {
        region.data = region.data.filter(day => day.cases !== undefined);
        region.data.forEach((dayData, dayIndex) => {
          if (dayIndex > 0) {
            dayData.new_cases = Math.max(0, dayData.cases - region.data[dayIndex - 1].cases)
            dayData.new_deaths = Math.max(0, dayData.deaths - region.data[dayIndex - 1].deaths)
          } else {
            dayData.new_cases = dayData.cases || 0;
            dayData.new_deaths = dayData.deaths || 0;
          }
          dayData.totalCases = dayData.cases + dayData.deaths

          const lastSevenDays = region.data.slice(Math.max(0, dayIndex - 7 + 1), dayIndex + 1)
          dayData.new_cases_7dayAverage = mean(lastSevenDays, d => d.new_cases)
          dayData.new_deaths_7dayAverage = mean(lastSevenDays, d => d.new_deaths)
          const lastFourteenDays = region.data.slice(Math.max(0, dayIndex - 14 + 1), dayIndex + 1)
          dayData.new_cases_14dayAverage = lastFourteenDays.map(d => d.new_cases).reduce((a, v) => a + v, 0);
          dayData.new_deaths_14dayAverage = lastFourteenDays.map(d => d.new_deaths).reduce((a, v) => a + v, 0);
        })
      })

      const subnationalUnits = regionArray
        .map(row => {
          const parent = getCountryByName(row['Country/Region']);
          if (parent) {
            const region = getSubnationalUnitByName(row['Province/State'], parent.id);
            if (region) {
              row.subnational = true
              row.region = region;
              row.id = row.region.id;
              row.name = row.region.name;
              if (!countryPopulations[row.id]) {
                console.warn(row);
                row.population = 'no data';
              } else {
                row.population = countryPopulations[row.id].population;
                row.parent = countryPopulations[row.id].parent;
              }
              return row;
            }
          }
          return null;
        }).filter(row => row);

      // US is first hardcoded value because it's data is separate
      const containsSubnational = ['840'].concat([...new Set(subnationalUnits.map(u => u.parent))]);

      const countries = regionArray.filter(d => !d.subnational && !d.aggregateRow)
          .map(row => {
            const country = getCountryByName(row.Combined_Key);
            row.country = country || { id: '000', name: row.Combined_Key, code: 'missing from countryLookup' };
            row.id = row.country.id;
            row.name = row.country.name;
            row.containsSubnational = containsSubnational.includes(row.id);
            if (!countryPopulations[row.id]){
              // console.warn(row);
              row.population = 'no data'; // filter?
            } else {
              row.population = countryPopulations[row.id].population;
            }
            return row;
          })
          .filter(row => row.population !== 'no data');

      const countryNonZeroValues = { deaths: [], cases: [] };
      countries.forEach(country => {
        const cases = country.data[country.data.length - 1].new_cases_14dayAverage
        const deaths =  country.data[country.data.length - 1].new_deaths_14dayAverage
        const perCapitaCases = cases / country.population;
        const perCapitaDeaths = deaths / country.population;
        countryNonZeroValues.cases.push(perCapitaCases);
        countryNonZeroValues.deaths.push(perCapitaDeaths);
      });
      setCountryNonZeroValues(countryNonZeroValues);

      //
      // Get Summary
      const flatRegionData = countries
        .map(region => region.data)
        .reduce((a, v) => a.concat(v), []);

      const allDays = sortBy([...new Set(countries.map(region => Object.keys(region.dataMap)).reduce((a, v) => a.concat(v), []))])
        .map(day => ({ dateString: day, date: new Date(day) }));

      const worldData = allDays.map(day => {
        const flatRegionDataByDay = flatRegionData
          .filter(regionDay => regionDay.dateString === day.dateString);
        return {
          date: day.date,
          dateString: day.dateString,
          cases: flatRegionDataByDay
            .map(region => region['cases'])
            .reduce((a, v) => a + v, 0),
          deaths: flatRegionDataByDay
            .map(region => region['deaths'])
            .reduce((a, v) => a + v, 0),
          new_cases: flatRegionDataByDay
            .map(region => region['new_cases'])
            .reduce((a, v) => a + v, 0),
          new_deaths: flatRegionDataByDay
            .map(region => region['new_deaths'])
            .reduce((a, v) => a + v, 0),
        }
      });
      worldData.forEach((datum, datumIndex) => {
        const lastSevenDays = worldData.slice(Math.max(0, datumIndex - 7 + 1), datumIndex + 1)
        datum.new_cases_7dayAverage = mean(lastSevenDays, d => d.new_cases)
        datum.new_deaths_7dayAverage = mean(lastSevenDays, d => d.new_deaths)
        const lastFourteenDays = worldData.slice(Math.max(0, datumIndex - 14 + 1), datumIndex + 1)
        datum.new_cases_14dayAverage = lastFourteenDays.map(d => d.new_cases).reduce((a, v) => a + v, 0);
        datum.new_deaths_14dayAverage = lastFourteenDays.map(d => d.new_deaths).reduce((a, v) => a + v, 0);
      });

      const lastDay = allDays[allDays.length - 1];
      const lastDayRegionData = flatRegionData
        .filter(regionDay => regionDay.dateString === lastDay.dateString);
      const cases = lastDayRegionData
        .map(region => region.cases)
        .reduce((a, v) => a + v, 0);
      const deaths = lastDayRegionData
        .map(region => region.deaths)
        .reduce((a, v) => a + v, 0);

      const population = countries.map(region => region.population).reduce((a, v) => a + v, 0);
      const worldSummary = {
        Combined_Key: 'world',
        key: 'world',
        name: 'world',
        cases,
        deaths,
        data: worldData,
        population,
      };

      countries.push(worldSummary);
      // Get Summary
      //

      // add state/province/territory level to data
      subnationalUnits.forEach(su => countries.push(su));

      const regionDataByKeyMap = {};
      countries.forEach(region => {
        regionDataByKeyMap[region.Combined_Key] = region;
      });
      setRegionDataByKey(regionDataByKeyMap);

      const regionsByIdMap = {};
      countries.forEach(region => {
        regionsByIdMap[region.id] = region;
      });
      setRegionsById(regionsByIdMap);

      setData(data => ({...data, 'hopkins-countries': countries, 'sub-national': subnationalUnits }))
    }

    if (countryPopulations) {
      const files = {};

      const checkIfDone = () => {
        numLoaded ++
        if (numLoaded === (hopkinsFiles.length * 2 + ukFiles.length)) {
          allLoaded(files);
        }
      }

      hopkinsFiles.forEach(file => {
        window.fetch(`${process.env.PUBLIC_URL}/data/time_series_covid19_${file}_global.csv`)
          .then(value => {
            return value.text()
          }).then(csv => {
            const rows = csvParse(csv).map(row => {
              if (row['Country/Region'] === 'United Kingdom') {
                row.ukPriority = 3;
              }
              return row;
            });
            if (!files[file]) {
              files[file] = []
            }
            files[file].push(...rows)

            checkIfDone()
          })
      })

      hopkinsFiles.forEach(file => {
        window.fetch(`${process.env.PUBLIC_URL}/data/jh_dailyfiles_global_summary_${file}.csv`)
          .then(value => {
            return value.text()
          }).then(csv => {
            const rows = csvParse(csv).map(row => {
              if (row['Country/Region'] === 'United Kingdom') {
                row.ukPriority = 2;
              }
              return row;
            });
            if (!files[file]) {
              files[file] = []
            }
            files[file].push(...rows)

            checkIfDone()
          })
      })

      ukFiles.forEach(file => {
        window.fetch(`${process.env.PUBLIC_URL}/data/uk-coronavirus-${file}_latest.csv`)
          .then(value => {
            return value.text()
          }).then(csv => {
            const rows = csvParse(csv).map(row => {
              if (row['Country/Region'] === 'United Kingdom') {
                row.ukPriority = 1;
              }
              return row;
            });
            const hopkinsFileName = file === 'cases' ? 'confirmed' : file
            if (!files[hopkinsFileName]) {
              files[hopkinsFileName] = []
            }
            files[hopkinsFileName].push(...rows)

            checkIfDone()
          })
      })

    }

  }, [countryPopulations, formatDateString])

  let content = props.defaultContent;

  if (data['hopkins-countries'] && data['sub-national'] && countryPopulations) {
    const anomalies = [
      {State: 'World', Date: '2020-12-10', Type: 'cases' },
    ]
    content = (
      <Switch>
        <Route path="/country/:id">
          <Viz
            type='world'
            zipcodes={[]}
            dateUpdated={dateUpdated}
            populations={countryPopulations}
            regionsById={regionsById}
            regionDataByKey={regionDataByKey}
            regionNonZeroValues={countryNonZeroValues}
            embedParams={props.embedParams}
            language={props.language}
            setLanguage={props.setLanguage}
            directionality={props.directionality}
            subnationalFeatures={subnationalFeatures}
            countries={countries}
            countriesHighRes={countriesHighRes}
            states={[]}
            counties={[]}
            countiesHighRes={[]}
            anomalies={anomalies}
          />
        </Route>
        <Route path="/">
          <Viz
            type='world'
            zipcodes={[]}
            dateUpdated={dateUpdated}
            populations={countryPopulations}
            regionsById={regionsById}
            regionDataByKey={regionDataByKey}
            regionNonZeroValues={countryNonZeroValues}
            embedParams={props.embedParams}
            language={props.language}
            setLanguage={props.setLanguage}
            directionality={props.directionality}
            subnationalFeatures={subnationalFeatures}
            countries={countries}
            countriesHighRes={countriesHighRes}
            states={[]}
            counties={[]}
            countiesHighRes={[]}
            anomalies={anomalies}
          />
        </Route>
      </Switch>
    )
  }

  return content;
}